import React, { useRef, useEffect, useState } from 'react';

/**
 * LatencySparkline Component
 * Renders a simple SVG sparkline graph for latency trends
 */
const LatencySparkline = ({ data, width = 400, height = 50 }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(width);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth || width);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [width]);

  const containerStyle = {
    width: '100%',
    height: `${height}px`,
    minWidth: 0,
    maxWidth: 'none',
    flex: '1 1 auto',
    overflow: 'hidden'
  };

  // Extract latency values
  const values = data && data.length > 0 ? data.map(d => d.avg_ms).filter(v => v != null) : [];
  
  // If no data, return null to allow other content to take the width
  if (!data || data.length === 0 || values.length === 0) {
    return null;
  }

  // Use actual container width for calculations
  const actualWidth = containerWidth || width;
  
  // Guard against invalid width
  if (!actualWidth || actualWidth <= 0 || isNaN(actualWidth)) {
    return null;
  }

  // Calculate min/max for scaling
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  
  // If all values are the same (or very close), add padding to show variation
  // This prevents flat lines when values are similar
  const padding = range < 0.01 ? Math.max(min * 0.1, 1) : 0; // 10% padding or 1ms minimum
  const adjustedMin = min - padding;
  const adjustedMax = max + padding;
  const adjustedRange = adjustedMax - adjustedMin || 1; // Avoid division by zero

  // Normalize values to 0-100% for rendering
  const normalizedValues = values.map(v => {
    const normalized = ((v - adjustedMin) / adjustedRange) * 100;
    return isNaN(normalized) ? 0 : normalized;
  });

  // Generate points for polyline using actual width
  const points = normalizedValues.map((value, index) => {
    const divisor = normalizedValues.length - 1 || 1;
    const x = (index / divisor) * actualWidth;
    const y = height - (value / 100) * height;
    // Guard against NaN values
    if (isNaN(x) || isNaN(y)) {
      return `0,${height}`;
    }
    return `${x},${y}`;
  }).join(' ');

  // Theme from config: .sla-sparkline-fill / .sla-sparkline-line use var(--color-primary) in ServiceLevelAgreement.css
  const areaPath = `M 0,${height} L ${points} L ${actualWidth},${height} Z`;

  return (
    <div ref={containerRef} className="sla-sparkline-container" style={containerStyle}>
      <svg width={actualWidth} height={height} viewBox={`0 0 ${actualWidth} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        <path d={areaPath} className="sla-sparkline-fill" />
        <polyline
          points={points}
          fill="none"
          className="sla-sparkline-line"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default LatencySparkline;

